import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { FoodIdSchema, FoodSizeLabelEnum } from './foods.js';
import { PlanStatusEnum } from './plan.js';

// ─── Enum schemas ─────────────────────────────────────────────────────────

// Order is significant: M4 commit 3's slot-budget allocator distributes
// macros against this exact ordering, and DietContentV1Schema's `slots`
// array refines on this sequence (see SLOT_ORDER below).
export const MealTypeEnum = z.enum(['breakfast', 'lunch', 'snacks', 'dinner']);
export type MealType = z.infer<typeof MealTypeEnum>;

const SLOT_ORDER = ['breakfast', 'lunch', 'snacks', 'dinner'] as const satisfies ReadonlyArray<
  z.infer<typeof MealTypeEnum>
>;

// ─── Macro shapes ─────────────────────────────────────────────────────────

// Daily targets, computed deterministically from the user profile in M4
// commit 3 (Mifflin-St Jeor → TDEE → goal-adjusted). The AI never sets
// these — it only sees them as input.
export const MacroTargetsV1Schema = z.object({
  daily_calories: z.number().int().min(800).max(6000),
  daily_protein: z.number().min(0).max(500),
  daily_carbs: z.number().min(0).max(1000),
  daily_fat: z.number().min(0).max(400),
});
export type MacroTargetsV1 = z.infer<typeof MacroTargetsV1Schema>;

export const SlotBudgetV1Schema = z.object({
  kcal: z.number().int().min(0).max(3000),
  protein: z.number().min(0).max(300),
  carbs: z.number().min(0).max(500),
  fat: z.number().min(0).max(200),
});
export type SlotBudgetV1 = z.infer<typeof SlotBudgetV1Schema>;

// ─── Slot foods ───────────────────────────────────────────────────────────

// One food line within a slot. `food_id` is the Foods DB FK; `food_name` is
// denormalized so the JSON content is self-contained for diff/render. Macros
// are server-recomputed from the Food row × `quantity_g` on every persist
// (UpdateSlotFoods recomputes), so a client cannot forge nutrition values.
export const DietSlotFoodV1Schema = z.object({
  id: IdSchema,
  food_id: FoodIdSchema,
  food_name: z.string().min(1).max(120),
  quantity_g: z.number().positive().max(2000),
  kcal: z.number().min(0).max(3000),
  protein: z.number().min(0).max(300),
  carbs: z.number().min(0).max(500),
  fat: z.number().min(0).max(200),
  // ADR-0015 unit-type fields. Optional + nullable so legacy V1 rows (no
  // unit fields) parse cleanly and Sonnet's M5.5-commit-5 output can emit
  // them. DietContentV1 stays at v1 — additive, backward-compatible.
  count: z.number().int().positive().nullable().optional(),
  size_label: FoodSizeLabelEnum.nullable().optional(),
  portion_label: FoodSizeLabelEnum.nullable().optional(),
});
export type DietSlotFoodV1 = z.infer<typeof DietSlotFoodV1Schema>;

export const DietSlotV1Schema = z.object({
  id: IdSchema,
  meal_type: MealTypeEnum,
  budget: SlotBudgetV1Schema,
  foods: z.array(DietSlotFoodV1Schema).min(1).max(10),
});
export type DietSlotV1 = z.infer<typeof DietSlotV1Schema>;

// ─── Plan content (versioned JSON shape) ──────────────────────────────────

const DietSlotsV1Schema = z
  .array(DietSlotV1Schema)
  .length(4)
  .refine((slots) => slots.every((s, i) => s.meal_type === SLOT_ORDER[i]), {
    message: `slots must be in order: ${SLOT_ORDER.join(', ')}`,
  })
  .superRefine(refineStableDietIds);

export const DietContentV1Schema = z.object({
  version: z.literal('v1'),
  targets: MacroTargetsV1Schema,
  slots: DietSlotsV1Schema,
  notes: z.array(z.string().max(280)).max(5).default([]),
});
export type DietContentV1 = z.infer<typeof DietContentV1Schema>;

// ─── DietContent V2 (M8 — archetype linkage + provenance) ────────────────
//
// V2 adds two fields on top of V1:
//   - archetypeId: which DietArchetype catalog row spawned this plan. Null
//     for legacy V1 plans (converter writes null) and for /diet/build custom
//     plans where the user composed slots manually.
//   - source: 'ai_generated' (existing M4/M5 path + archetype-materialized
//     plans) | 'user_built' (M8 custom builder).
// Slot shape is unchanged, so DietSlotV1Schema is reused.
/** Diet plan content v2: V1 + archetypeId + source. */
export const DietContentV2Schema = z.object({
  version: z.literal('v2'),
  targets: MacroTargetsV1Schema,
  slots: DietSlotsV1Schema,
  notes: z.array(z.string().max(280)).max(5).default([]),
  archetypeId: z.string().min(1).max(80).nullable(),
  source: z.enum(['ai_generated', 'user_built']).default('ai_generated'),
});
export type DietContentV2 = z.infer<typeof DietContentV2Schema>;

function refineStableDietIds(
  slots: Array<{ id: string; foods: Array<{ id: string }> }>,
  ctx: z.RefinementCtx,
): void {
  const slotIds = new Set<string>();
  const foodInstanceIds = new Set<string>();

  slots.forEach((slot, slotIndex) => {
    if (slotIds.has(slot.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [slotIndex, 'id'],
        message: 'slot IDs must be unique within a diet plan',
      });
    }
    slotIds.add(slot.id);

    slot.foods.forEach((food, foodIndex) => {
      if (foodInstanceIds.has(food.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [slotIndex, 'foods', foodIndex, 'id'],
          message: 'food instance IDs must be unique within a diet plan',
        });
      }
      foodInstanceIds.add(food.id);
    });
  });
}

/** Convert a legacy V1 diet plan to V2 shape (archetype unknown → null). */
export function convertDietContentV1ToV2(v1: DietContentV1): DietContentV2 {
  return {
    ...v1,
    version: 'v2' as const,
    archetypeId: null,
    source: 'ai_generated' as const,
  };
}

/** Discriminated union that decodes either V1 or V2 diet plan rows. */
export const DietContentAnySchema = z.discriminatedUnion('version', [
  DietContentV1Schema,
  DietContentV2Schema,
]);
export type DietContentAny = z.infer<typeof DietContentAnySchema>;

// ─── Diet response shape ──────────────────────────────────────────────────

// `content` accepts either V1 (legacy plans persisted by M4-M5.6) or V2
// (M8.7+ plans with archetypeId + source). DietContentAnySchema is a
// discriminated union on `version`, so client code branches deterministically.
export const DietResponseSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  type: z.literal('nutrition'),
  status: PlanStatusEnum,
  startsOn: IsoDateSchema,
  endsOn: IsoDateSchema,
  content: DietContentAnySchema,
  modelUsed: z.string(),
  promptVersion: z.string(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
});
export type DietResponse = z.infer<typeof DietResponseSchema>;

// ─── Request / response DTOs ──────────────────────────────────────────────

// M8.7 — POST /v1/ai/diet/generate now accepts an optional archetypeId so
// the recommendation flow can request a generation tied to a specific
// DietArchetype catalog row. Body remains backward-compatible: legacy
// callers may POST with no body or with `{}`, in which case the server
// runs the pre-M8 general-profile-driven pipeline and writes archetypeId =
// null on the persisted DietContentV2 row.
export const GenerateDietRequestSchema = z.object({
  archetypeId: z.string().min(1).max(80).nullable().optional(),
});
export type GenerateDietRequest = z.infer<typeof GenerateDietRequestSchema>;

export const GenerateDietResponseSchema = z.object({
  diet: DietResponseSchema,
});
export type GenerateDietResponse = z.infer<typeof GenerateDietResponseSchema>;

export const GetDietResponseSchema = z.object({
  diet: DietResponseSchema.nullable(),
});
export type GetDietResponse = z.infer<typeof GetDietResponseSchema>;

export const AcceptDietRequestSchema = z.object({
  dietId: IdSchema,
});
export type AcceptDietRequest = z.infer<typeof AcceptDietRequestSchema>;

export const AcceptDietResponseSchema = z.object({
  diet: DietResponseSchema,
});
export type AcceptDietResponse = z.infer<typeof AcceptDietResponseSchema>;

// Per-food entry in the PATCH /slot wire shape. ADR-0015 mode-aware:
//   - count   foods → { food_id, count, size_label? }
//   - portion foods → { food_id, portion_label }
//   - grams   foods → { food_id, quantity_g }
// All four input fields are optional at the Zod layer; the API service runs
// the same normalizeSlotFoods helper that diet generation uses, which
// validates mode-vs-unitType against the persisted catalog and throws
// ValidationError → 400 on mismatch. Backward compat: a client sending
// `{ food_id, quantity_g }` continues to work for grams-mode foods.
export const UpdateSlotFoodEntrySchema = z.object({
  food_id: FoodIdSchema,
  quantity_g: z.number().positive().max(2000).optional(),
  count: z.number().int().positive().optional(),
  size_label: FoodSizeLabelEnum.optional(),
  portion_label: FoodSizeLabelEnum.optional(),
});
export type UpdateSlotFoodEntry = z.infer<typeof UpdateSlotFoodEntrySchema>;

export const UpdateSlotFoodsRequestSchema = z.object({
  slotId: IdSchema,
  foods: z.array(UpdateSlotFoodEntrySchema).min(1).max(10),
});
export type UpdateSlotFoodsRequest = z.infer<typeof UpdateSlotFoodsRequestSchema>;

export const UpdateSlotFoodsResponseSchema = z.object({
  diet: DietResponseSchema,
});
export type UpdateSlotFoodsResponse = z.infer<typeof UpdateSlotFoodsResponseSchema>;
